import zIndex from '@mui/material/styles/zIndex';
import { AUTHORITIES } from 'app/config/constants';
import { REACT_APP_MAP_KEY } from 'app/config/map-key';
import { useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { mapStyle } from 'app/shared/layout/maps/CustomStyle';
import { RouteAddressType } from 'app/shared/model/enumerations/RouteAddressType.model';
import { PeriodicType } from 'app/shared/model/enumerations/periodicType';
import { getAddress } from 'app/shared/reducers/geocode.reducer';
import { makeTruckImplementMapIcon } from 'app/shared/util/truck-implement-map-icon';
import { convertDateTimeToPtBRWithYear, formatIdleTime } from 'app/shared/util/date-utils';
import { STATUS_COLORS } from 'app/modules/dashboards/dashboard-company/components/company-truck-implements-tab/company-vehicle-list/constants';
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { GoogleMap, Marker, Polyline, withGoogleMap, withScriptjs } from 'react-google-maps';
import InfoWindow from 'react-google-maps/lib/components/InfoWindow';
import StreetViewPanorama from 'react-google-maps/lib/components/StreetViewPanorama';
import TrafficLayer from 'react-google-maps/lib/components/TrafficLayer';
import MarkerClusterer from 'react-google-maps/lib/components/addons/MarkerClusterer';
import { Translate } from 'react-jhipster';
import { Spinner } from 'reactstrap';
import { compose, withProps } from 'recompose';
import {
  IIdlePositions,
  IInfractions,
  ILastPosition,
  IPosition,
  IRouteInfo,
  ITruckImplementJourneyMapProps,
} from '../../interface/dashboardTruckImplementInterface';
import '../../styles/dashboards.scss';
import { RoutesCollapse } from './RoutesCollapse';
import { UserMapSetupCollapse } from './UserMapSetupCollapse';
import { IdStorageTypes, ValuesStorageTypes, getStorageOptions, setStorageOptions } from './storageOptions';

declare const google: any;

const TruckImplementJourneyMap = (props: ITruckImplementJourneyMapProps) => {
  const [listInfractions, setListInfractions] = useState<IInfractions[] | null>();
  const [routePolyline, setRoutePolyline] = useState<IPosition[]>();
  const [infoSubRoute, setInfoSubRoute] = useState<IRouteInfo>();
  const [checkedTraffic, setCheckedTraffic] = useState<boolean>(getStorageOptions(IdStorageTypes.TRAFFIC) ?? false);
  const [checkedRoutes, setCheckedRoutes] = useState<boolean>(getStorageOptions(IdStorageTypes.ROUTES) ?? true);
  const [checkedInfoRoute, setCheckedInfoRoute] = useState<boolean>(getStorageOptions(IdStorageTypes.INFO_ROUTE) ?? false);
  const [mapType, setMapType] = useState<ValuesStorageTypes>(getStorageOptions(IdStorageTypes.MAP_TYPE) ?? ValuesStorageTypes.MAP);
  const [onCenterMarkerPosition, setOnCenterMarkerPosition] = useState<string | null>(null);
  const [popoverKeyColumnAlert, setPopoverKeyColumnAlert] = useState<number>(null);
  const [popoverLastPosition, setPopoverLastPosition] = useState<boolean>(false);
  const [popoverKeyLineAlert, setPopoverKeyLineAlert] = useState<number>(null);
  const [zoomMarkerClusterer, setZoomMarkerClusterer] = useState<number>(0);
  const [satelliteMapType, setSatelliteMapType] = useState<boolean>(false);
  const [routeAlerts, setRouteAlerts] = useState<IPosition[]>();
  const [onStreetView, setOnStreetView] = useState<boolean>(false);
  const [alertPopover, setAlertPopover] = useState<boolean>(false);
  const [hoveredMarker, setHoveredMarker] = useState<number>(null);
  const [markerIdlePosition, setMarkerIdlePosition] = useState<number>(null);
  const [renderMarkerIdlePosition, setRenderMarkerIdlePosition] = useState<boolean>(false);
  const [loadingInfo, setLoadingInfo] = useState<boolean>(false);
  const [alertAddress, setAlertAddress] = useState<string>('');
  const [startIndex, setStartIndex] = useState<number>(null);
  const [finalIndex, setFinalIndex] = useState<number>(null);
  const [idlePositions, setIdlePositions] = useState<IIdlePositions[]>([]);
  const [isReportLoading, setIsReportLoading] = useState<boolean>(false);

  const showReportLoading = useCallback(
    (value: boolean) => {
      setIsReportLoading(value);
    },
    [isReportLoading]
  );

  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));

  const mapRef = useRef(null);
  const mapInitialPosition = [
    { lat: 4.326383914161625, lng: -55.787664895705404 },
    { lat: -31.40830039764787, lng: -52.71149302062307 },
  ];

  const changeIconSize = useCallback(
    (sizeOne: number, sizeTwo: number) => {
      if (onStreetView) {
        return { firstSize: sizeOne, secondSize: sizeOne };
      }
      return { firstSize: sizeTwo, secondSize: sizeTwo };
    },
    [onStreetView]
  );

  const lastPositionTruckImplementIcon = isActivated =>
    makeTruckImplementMapIcon(isActivated ? STATUS_COLORS.successAlt : STATUS_COLORS.muted, props.licensePlate ?? '');

  const startPositionIcon = {
    url: 'content/images/map-icons/icon_dot_start.png',
    scaledSize: new google.maps.Size(changeIconSize(80, 19).firstSize, changeIconSize(80, 19).secondSize),
    anchor: new google.maps.Point(9.5, 9.5),
  };

  const finalPositionIcon = {
    url: 'content/images/map-icons/icon_dot_final.png',
    scaledSize: new google.maps.Size(changeIconSize(80, 19).firstSize, changeIconSize(80, 19).secondSize),
    anchor: new google.maps.Point(9.5, 9.5),
  };

  const centerPositionAlertIcon = {
    url: 'content/images/map-icons/icon_center_position.png',
    scaledSize: new google.maps.Size(changeIconSize(0, 19).firstSize, changeIconSize(0, 19).secondSize),
    anchor: new google.maps.Point(8, 8),
  };

  const arrowIcon = {
    path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
    fillColor: 'red',
    fillOpacity: 1,
    scale: 3,
    strokeColor: 'red',
    strokeWeight: 1,
  };

  const clustererStyle = [
    {
      url: 'content/images/map-icons/icon_dot_cluster_green.png',
      textColor: 'white',
      textSize: 15,
      width: 51,
      height: 50,
    },
  ];

  const lineSymbol = {
    path: 'M 0,-1 0,1',
    strokeOpacity: 1,
    strokeWeight: 2,
    scale: 2,
  };

  const chooseIcon = (alertType: string) => {
    return {
      url: `content/images/map-icons/${'icon_pin_' + alertType?.toLowerCase()}.png`,
      scaledSize: new google.maps.Size(changeIconSize(80, 35).firstSize, changeIconSize(80, 35).secondSize),
    };
  };

  const renderAlertPopover = (keyColumn: number, keyLine: number) => {
    setPopoverKeyColumnAlert(keyColumn);
    setPopoverKeyLineAlert(keyLine);
    setAlertPopover(!alertPopover);
  };

  const getAlertAddress = (lat: number, lon: number) => {
    getAddress(lat, lon).then(resp => {
      setAlertAddress(resp.data.address);
    });
  };

  const calcIndexMarker = useCallback(
    (infraction: IInfractions, index: number, type: string) => {
      const angle = ((360.0 / infraction.alerts.length) * index) / 180;

      if (type === 'lat') {
        return infraction.lat + (satelliteMapType ? 0.0002 : 0.00006) * Math.sin(+angle * Math.PI);
      }
      return infraction.lng + (satelliteMapType ? 0.0002 : 0.00006) * Math.cos(+angle * Math.PI);
    },
    [satelliteMapType]
  );

  function handleZoomChanged() {
    setZoomMarkerClusterer(this.getZoom());
    props.setZoom(this.getZoom());
  }

  const renderAlertMarkers = useCallback(() => {
    return (
      <MarkerClusterer styles={clustererStyle} maxZoom={satelliteMapType ? 17 : 19}>
        {listInfractions &&
          listInfractions.map((infraction, column) => {
            return (
              <Fragment key={column}>
                {infraction.alerts.map((alert, line) => {
                  return (
                    <Fragment key={line}>
                      <Marker
                        icon={chooseIcon(alert.alertType)}
                        position={{
                          lat: calcIndexMarker(infraction, line, 'lat'),
                          lng: calcIndexMarker(infraction, line, 'lng'),
                        }}
                        onClick={() => [renderAlertPopover(column, line), getAlertAddress(infraction.lat, infraction.lng)]}
                      >
                        {alertPopover && popoverKeyColumnAlert === column && popoverKeyLineAlert === line && (
                          <InfoWindow onCloseClick={() => renderAlertPopover(column, line)}>
                            <dl className="m-0 text-left">
                              <dt>Alerta:</dt>
                              <dd>{alert.alertName}</dd>
                              <dt>Tipo:</dt>
                              <dd>{<Translate contentKey={`fleetSenseApp.AlertType.${alert.alertType}`} />}</dd>
                              <dt>Valor Alerta:</dt>
                              <dd>{alert.alertValue}</dd>
                              <dt>Valor Sensor:</dt>
                              <dd>{alert.sensorValue}</dd>
                              <dt>Data | Hora:</dt>
                              <dd>{convertDateTimeToPtBRWithYear(alert.created)}</dd>
                              <dt>Endereço:</dt>
                              <dd>{alertAddress}</dd>
                            </dl>
                          </InfoWindow>
                        )}
                      </Marker>
                      {infraction.alerts.length > 1 && zoomMarkerClusterer > (satelliteMapType ? 17 : 19) && (
                        <Fragment>
                          <Polyline
                            path={[
                              { lat: infraction.lat, lng: infraction.lng },
                              { lat: calcIndexMarker(infraction, line, 'lat'), lng: calcIndexMarker(infraction, line, 'lng') },
                            ]}
                            options={{
                              strokeOpacity: 0,
                              icons: [
                                {
                                  icon: lineSymbol,
                                  repeat: '10px',
                                },
                              ],
                            }}
                          ></Polyline>
                          <Marker icon={centerPositionAlertIcon} position={{ lat: infraction.lat, lng: infraction.lng }} />
                        </Fragment>
                      )}
                    </Fragment>
                  );
                })}
              </Fragment>
            );
          })}
      </MarkerClusterer>
    );
  }, [
    zoomMarkerClusterer,
    listInfractions,
    alertPopover,
    popoverKeyColumnAlert,
    popoverKeyLineAlert,
    alertAddress,
    startIndex,
    satelliteMapType,
    infoSubRoute,
    onStreetView,
  ]);

  const renderLastPositionMarker = (lastPosition: ILastPosition, period: PeriodicType) => {
    return (
      lastPosition &&
      period === PeriodicType.TODAY && (
        <Marker
          icon={lastPositionTruckImplementIcon(lastPosition.isActivated)}
          position={{ lat: lastPosition.lat, lng: lastPosition.lng }}
          onClick={() => setPopoverLastPosition(!popoverLastPosition)}
          options={{ zIndex: zIndex.drawer }}
        >
          {popoverLastPosition && (
            <InfoWindow onCloseClick={() => setPopoverLastPosition(!popoverLastPosition)}>
              <dl className="m-0" style={{ textAlign: 'left' }}>
                <dt className="mb-1">
                  <span id="address">
                    <Translate contentKey="fleetSenseApp.alertActivation.home.lastPosition">No Alert Activations found</Translate>
                  </span>
                </dt>
                <dd>{lastPosition.address}</dd>
                <dd>{convertDateTimeToPtBRWithYear(lastPosition.date)}</dd>
              </dl>
            </InfoWindow>
          )}
        </Marker>
      )
    );
  };

  useEffect(() => {
    if (routePolyline?.length && startIndex !== null && finalIndex !== null) {
      const partialPolyline = routePolyline.slice(startIndex, finalIndex);

      if (!partialPolyline?.length) return;

      setRouteAlerts(partialPolyline);
    } else if (routePolyline?.length) {
      setRouteAlerts(routePolyline);
    }
  }, [routePolyline, startIndex, finalIndex]);

  const renderPartialRoute = useCallback(() => {
    if (!routePolyline?.length) return;

    const partialPolyline = routePolyline.slice(startIndex, finalIndex);

    if (!partialPolyline?.length) return;

    const finalPosition = partialPolyline[partialPolyline.length - 1];
    const initialPosition = partialPolyline[0];

    return (
      <Fragment>
        <Polyline
          options={{
            strokeColor: '#000814',
            strokeWeight: 6,
            geodesic: true,
            icons: [
              {
                icon: finalIndex && arrowIcon,
                repeat: '80px',
              },
            ],
          }}
          path={partialPolyline}
        />
        <Marker
          position={initialPosition}
          icon={startPositionIcon}
          title="Partida"
          options={{
            zIndex: zIndex.drawer,
          }}
        />
        <Marker
          position={finalPosition}
          icon={finalPositionIcon}
          title="Chegada"
          options={{
            zIndex: zIndex.drawer,
          }}
        />
      </Fragment>
    );
  }, [routePolyline, startIndex, finalIndex, onCenterMarkerPosition, onStreetView]);

  const renderIdlePositions = useCallback(() => {
    if (!idlePositions?.length) return;

    return (
      <Fragment>
        {idlePositions.map((item, index) => (
          <Marker
            key={index}
            position={{ lat: item.lat, lng: item.lng }}
            onClick={() => setMarkerIdlePosition(index)}
            icon={{
              url: `content/images/map-icons/icon_pin_${(() => {
                if (item.idleTimeInSeconds > 600) return 'serious';
                if (item.idleTimeInSeconds > 120) return 'medium';
                return 'light';
              })()}.png`,
              scaledSize: new google.maps.Size(changeIconSize(80, 35).firstSize, changeIconSize(80, 35).secondSize),
            }}
          >
            {markerIdlePosition === index && (
              <InfoWindow onCloseClick={() => setMarkerIdlePosition(null)}>
                <dl className="text-left">
                  <dt>Tempo ocioso:</dt>
                  <dd>{formatIdleTime(item.idleTimeInSeconds)}</dd>
                  <dt>Data de início</dt>
                  <dd>{convertDateTimeToPtBRWithYear(item.idleStart)}</dd>
                  <dt>Data de fim</dt>
                  <dd>{convertDateTimeToPtBRWithYear(item.idleEnd)}</dd>
                </dl>
              </InfoWindow>
            )}
          </Marker>
        ))}
      </Fragment>
    );
  }, [idlePositions, markerIdlePosition]);

  const renderEntireRoute = useCallback(() => {
    if (!routePolyline?.length || props.loading || finalIndex) return;

    return (
      <Polyline
        options={{
          strokeColor: '#000814',
          strokeWeight: 6,
          strokeOpacity: !finalIndex ? 1 : 0.5,
          geodesic: true,
          icons: [
            {
              icon: !finalIndex && arrowIcon,
              repeat: '80px',
            },
          ],
        }}
        path={routePolyline}
      />
    );
  }, [routePolyline, finalIndex, props.loading, arrowIcon]);

  const getFilteredMarkers = useCallback(() => {
    if (!routeAlerts?.length || props.loading) return;

    const totalMarkers = routeAlerts.length;

    const zoomPercentages = [
      { maxZoom: 5, percentage: 0.0005 },
      { maxZoom: 8, percentage: 0.001 },
      { maxZoom: 10, percentage: 0.005 },
      { maxZoom: 12, percentage: 0.01 },
      { maxZoom: 13, percentage: 0.05 },
      { maxZoom: 14, percentage: 0.06 },
      { maxZoom: 15, percentage: 0.07 },
      { maxZoom: 16, percentage: 0.1 },
      { maxZoom: 17, percentage: 0.3 },
      { maxZoom: 18, percentage: 0.5 },
      { maxZoom: Infinity, percentage: 1 },
    ];

    const { percentage } = zoomPercentages.find(({ maxZoom }) => zoomMarkerClusterer <= maxZoom) || { percentage: 1 };

    const markersToDisplay = Math.ceil(totalMarkers * percentage);

    const step = Math.max(1, Math.floor(totalMarkers / markersToDisplay));

    return routeAlerts.filter((_, index) => index % step === 0);
  }, [routeAlerts, zoomMarkerClusterer, props.loading]);

  const renderMarkers = useCallback(() => {
    if (!getFilteredMarkers()?.length) return null;

    return getFilteredMarkers().map((point, index) =>
      point.lat && point.lng && point.odometer ? (
        <Marker
          key={index}
          position={{ lat: point.lat, lng: point.lng }}
          onClick={() => setHoveredMarker(index)}
          icon={{
            url: `content/images/map-icons/icon_pin_info.png`,
          }}
        >
          {hoveredMarker === index && (
            <InfoWindow key={index} onCloseClick={() => setHoveredMarker(null)}>
              <dl className="text-left">
                <dt>Data | Hora:</dt>
                <dd>{convertDateTimeToPtBRWithYear(point.created)}</dd>
                <dt>Odômetro:</dt>
                <dd>{point.odometer} km</dd>
                <dt>Velocidade:</dt>
                <dd>{point.speed} km/h</dd>
                {isAdmin && (
                  <>
                    <dt>Tensão da Bateria:</dt>
                    <dd>{point.vBat} v</dd>
                    <dt>Tensão de Alimentação:</dt>
                    <dd>{point.vSupply} v</dd>
                    <dt>Alimentação Ligada:</dt>
                    <dd>{point.supplyOn ? 'Sim' : 'Não'}</dd>
                    <dt>Precisão GPS:</dt>
                    <dd>{point.gpsFix ? 'Sim' : 'Não'}</dd>
                  </>
                )}
              </dl>
            </InfoWindow>
          )}
        </Marker>
      ) : null
    );
  }, [getFilteredMarkers, hoveredMarker]);

  const renderButtonRoutes = useCallback(() => {
    const getDivButtonRoutes = document.querySelector('.div-button-routes');
    const getDivButtonOptions = document.querySelector('.div-button-options');

    const divMap = document.querySelector('.gm-style');

    if (!getDivButtonRoutes || !getDivButtonOptions) {
      const divButton = document.createElement('div');
      const divButtonOptions = document.createElement('div');

      divButton.classList.add('div-button-routes');
      divButtonOptions.classList.add('div-button-options');

      divMap?.append(divButton);
      divMap?.append(divButtonOptions);
    }

    if (getDivButtonRoutes && !props.loading && props.routes?.length) {
      ReactDOM.render(
        <RoutesCollapse
          routes={props.routes}
          positions={props.positions}
          isBetaVersion={props.isBetaVersion}
          loadingInfo={loadingInfo}
          periodType={props.periodType}
          setStartIndex={setStartIndex}
          setFinalIndex={setFinalIndex}
          setIdlePositions={setIdlePositions}
          implementId={props.implementId}
          setLoadingInfo={setLoadingInfo}
          setInfoSubRoute={setInfoSubRoute}
          listInfractions={listInfractions}
          setListInfractions={setListInfractions}
          setOnCenterMarkerPosition={setOnCenterMarkerPosition}
          setRenderMarkerIdlePosition={setRenderMarkerIdlePosition}
          showReportLoading={showReportLoading}
          isReportLoading={isReportLoading}
        />,
        getDivButtonRoutes
      );
    } else if (divMap && getDivButtonRoutes) {
      divMap.removeChild(getDivButtonRoutes);
    }

    if (!props.loading && getDivButtonOptions) {
      ReactDOM.render(
        <UserMapSetupCollapse
          checkedRoutes={checkedRoutes}
          setCheckedRoutes={setCheckedRoutes}
          checkedTraffic={checkedTraffic}
          setCheckedTraffic={setCheckedTraffic}
          checkedInfoRoute={checkedInfoRoute}
          setCheckedInfoRoute={setCheckedInfoRoute}
          periodType={props.periodType}
          isBetaVersion={props.isBetaVersion}
          routes={props.routes}
        />,
        getDivButtonOptions
      );
    } else if (divMap && getDivButtonOptions) {
      divMap.removeChild(getDivButtonOptions);
    }
  }, [props.routes, props.implementId, props.periodType, props.loading, checkedTraffic, checkedRoutes, loadingInfo, listInfractions, checkedInfoRoute]);

  const renderLoading = useCallback(
    () =>
      props.loading && (
        <div className="spinner-map">
          <Spinner color="#000814" />
        </div>
      ),
    [props.loading]
  );

  const reportLoading = useCallback(
    () =>
      isReportLoading && (
        <div className="spinner-map">
          <Spinner color="#000814" />
        </div>
      ),
    [isReportLoading]
  );

  function onClickMapType() {
    document.getElementsByClassName('gm-style-mtc')[0].addEventListener('click', () => {
      setSatelliteMapType(false);
      setMapType(ValuesStorageTypes.MAP);
      setStorageOptions(IdStorageTypes.MAP_TYPE, ValuesStorageTypes.MAP);
    });
    document.getElementsByClassName('gm-style-mtc')[1].addEventListener('click', () => {
      setSatelliteMapType(true);
      setMapType(ValuesStorageTypes.SATELLITE);
      setStorageOptions(IdStorageTypes.MAP_TYPE, ValuesStorageTypes.SATELLITE);
    });
  }

  function onChangeStreetView() {
    setOnStreetView(this.visible);
  }

  const setFitBounds = () => {
    let pathPolyline = [];

    if (listInfractions?.length) {
      listInfractions.forEach(infraction => {
        pathPolyline.push({ lat: infraction.lat, lng: infraction.lng });
      });
    } else if (!routePolyline?.length) {
      pathPolyline = mapInitialPosition;
    } else if (onCenterMarkerPosition && startIndex !== null && finalIndex !== null) {
      const partialPolyline = routePolyline.slice(startIndex, finalIndex);
      if (!partialPolyline?.length) return;

      pathPolyline = [partialPolyline[0], partialPolyline[partialPolyline.length - 1]];

      if (onCenterMarkerPosition === RouteAddressType.START) {
        pathPolyline = [pathPolyline[0]];
      } else if (onCenterMarkerPosition === RouteAddressType.FINAL) {
        pathPolyline = [pathPolyline[1]];
      }
    } else if (startIndex !== null && finalIndex !== null) {
      pathPolyline = routePolyline.slice(startIndex, finalIndex);

      if (!pathPolyline?.length) return;
      pathPolyline = [pathPolyline[0], pathPolyline[pathPolyline.length - 1]];
    } else if (routePolyline?.length) {
      pathPolyline = [routePolyline[0], routePolyline[routePolyline.length - 1]];
    }

    const bounds = new window.google.maps.LatLngBounds();
    pathPolyline.map(item => {
      if (item.lat && item.lng) {
        bounds.extend({ lat: item.lat, lng: item.lng });
        mapRef.current.fitBounds(bounds);
      }
    });
  };

  useEffect(() => {
    setFitBounds();
  }, [routePolyline, startIndex, finalIndex, onCenterMarkerPosition, props.periodType, props.loading, listInfractions]);

  useEffect(() => {
    setRoutePolyline(props.positions);
  }, [props.positions, props.periodType]);

  return (
    <GoogleMap
      ref={mapRef}
      zoom={props.zoom}
      center={props.center}
      defaultOptions={{ styles: mapStyle }}
      onZoomChanged={handleZoomChanged}
      onMapTypeIdChanged={onClickMapType}
      onTilesLoaded={renderButtonRoutes}
      mapTypeId={google.maps.MapTypeId[mapType]}
    >
      {checkedTraffic && props.periodType === PeriodicType.TODAY && <TrafficLayer />}
      <StreetViewPanorama visible={false} onVisibleChanged={onChangeStreetView} />
      {[renderButtonRoutes(), renderLoading(), reportLoading()]}
      {checkedRoutes && renderAlertMarkers()}
      {checkedRoutes && renderEntireRoute()}
      {checkedRoutes && renderPartialRoute()}
      {renderMarkerIdlePosition && renderIdlePositions()}
      {checkedInfoRoute && renderMarkers()}
      {!finalIndex && !props.loading && renderLastPositionMarker(props.lastPosition, props.periodType)}
    </GoogleMap>
  );
};

const G_API_URL = `https://maps.googleapis.com/maps/api/js?key=${REACT_APP_MAP_KEY}&&v=3.exp&libraries=geometry,drawing,places`;

export default compose(
  withProps({
    googleMapURL: G_API_URL,
    loadingElement: <div style={{ height: `100%` }} />,
    containerElement: <div style={{ height: `calc(100vh - 220px)` }} />,
    mapElement: <div style={{ height: `100%`, borderRadius: `8px` }} />,
  }),
  withScriptjs,
  withGoogleMap
)(TruckImplementJourneyMap);
